import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { combineReducers } from 'redux-immutable';
import { OneFetchyeProvider, OneCache } from 'fetchye-one-app';
import { composeModules, RenderModule } from 'holocron';
import SharedScript from '@americanexpress/shared-scripts';
import childRoutes from '@/childRoutes';
import {
  loadOneCmsLocaleCommonContent,
  loadOneCmsLocaleProductContent,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlFetchyeHooks';
import {
  loadCdaasCulinaryCollectiveContent,
  loadCdaasCenturionHomepageContent,
} from '@/hooks/cdaasHooks/cdaasFetchyeHooks';
import { routeParamsContext } from '@/context/routeParamsContext';
import { queryParamsContext } from '@/context/queryParamsContext';
import { ModalProvider } from '@/providers/ModalProvider';
import {
  loadOneCmsLocaleAirports,
  loadBdaasAirports,
} from '@/hooks/airportHooks/airportsFetchyeHooks';
import {
  loadBdaasAirportLounges,
  loadOneCmsLocaleAirportLounges,
  loadBdaasAirportLoungesByExperienceType,
} from '@/hooks/loungesHooks/loungesFetchyeHooks';
import authReducer from '@/slices/authSlice';
import lastViewedProductReducer, {
  setLastViewedProduct,
} from '@/slices/lastViewedProductSlice';
import { useCenturionForcedLoginRedirection } from '@/hooks/forcedLoginHooks';
import { useHeaderLoginListener } from '@/hooks/useHeaderLoginListener';
import { useBestCardRedirection } from '@/hooks/useBestCardRedirection';
import CustomTrackProvider from '@/providers/CustomTrackProvider';
import loungeReducer from '@/slices/loungeSlice';
import walletReducer from '@/slices/walletSlice';
import { Card } from '@/constants';
import { isAuthenticatedAndLoadWallet } from '@/thunks/checkIsLoggedInThunk';
import { getNewDomainUrl } from '@/selectors/configSelectors';
import { getUrlWithTrailingSlash } from '@/helpers/utils';
import { canViewTCL } from '@/helpers/featureFlagHelper';
import { TclMigrationFeatures } from '@/hooks/featureFlagHooks';
import ScrollToTopOnLocationChange from './common/ScrollToTopOnLocationChange';
import LoungeFinderRequiredDataProvider from './LoungeFinderRequiredDataProvider';
import { MainHelmet, SEOHelmet } from './common/LoungeFinderHelmets';
import ErrorReporter from './ErrorReporter';
import AnalyticsWrapper from './AnalyticsWrapper';
import GlobalHeader from './common/header/GlobalHeader';
// import mockData from '@/mockData'; // uncomment to use mock data
import Footer from './common/Footer';
import LoungeFinderErrorBoundary from './LoungeFinderErrorBoundary';
import TopBanner from './common/TopBanner';
import RootModal from './common/modals/RootModal';
import './common/GlobalOneNavOverrides.scss';

if (global.BROWSER) {
  window.useNativeIntl = true;
}

const TravelLoungeFinderRoot = ({
  locale,
  children: routeChildren,
  params,
  location,
}) => {
  const memoizedRouteParams = React.useMemo(
    () => ({
      ...params,
      loungeId: params?.loungeId?.split(/-+/).pop(),
      pathname: location?.pathname,
      locale: params?.locale,
    }),
    [params, location]
  );
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (Object.values(Card).includes(params?.product)) {
      dispatch(setLastViewedProduct(params.product));
    }
  }, [dispatch, params?.product]);

  const domain = useSelector(getNewDomainUrl);
  const pathName = location?.pathname ?? '/';
  const url = new URL(`${pathName}${location?.search}`, domain);
  const fullUrl = url.toString();

  useHeaderLoginListener('#gnav_login');
  useBestCardRedirection();
  useCenturionForcedLoginRedirection({
    params,
    locale,
  });
  const bannerConfig = {
    header: 'LoungeBuddy is no longer available as of January 30, 2025',
    content:
      "Welcome to Lounge Finder! You've been redirected from LoungeBuddy to the American Express Lounge Finder, where eligible American Express Card Members can find details on airport lounges they can access with select American Express Card Products.",
  };

  const locationPathname = location?.pathname;
  const locationSearch = location?.search;
  const locationHash = location?.hash;

  const newUrl = React.useMemo(() => {
    if (global.BROWSER && locationPathname) {
      return getUrlWithTrailingSlash(
        locationPathname,
        locationSearch,
        locationHash
      );
    }
    return null;
  }, [locationPathname, locationSearch, locationHash]);

  // Prevent excessive replaceState calls by tracking previous URL
  const prevUrlRef = useRef();
  React.useEffect(() => {
    if (newUrl && prevUrlRef.current !== newUrl) {
      window.history.replaceState({}, '', newUrl);
      prevUrlRef.current = newUrl;
    }
  }, [newUrl]);

  return (
    <ErrorReporter>
      <OneFetchyeProvider>
        <queryParamsContext.Provider value={location?.search}>
          <routeParamsContext.Provider value={memoizedRouteParams}>
            <IntlProvider locale={locale}>
              <AnalyticsWrapper location={location}>
                <SharedScript
                  name="one-identity-session"
                  version="^1.0.0"
                  windowCheck="AmexSession"
                />
                <MainHelmet />
                <ScrollToTopOnLocationChange />
                <CustomTrackProvider>
                  <ModalProvider>
                    <RenderModule
                      moduleName="axp-global-header"
                      props={{
                        loginDestPage: fullUrl,
                        logoutDestPage: fullUrl,
                      }}
                    />
                    <LoungeFinderErrorBoundary>
                      <SEOHelmet location={location} />
                      <LoungeFinderRequiredDataProvider>
                        <TopBanner {...bannerConfig} />
                        <main id="mainContent" tabIndex={-1}>
                          <GlobalHeader location={location} />
                          {routeChildren}
                          <Footer />
                        </main>
                        <RootModal />
                      </LoungeFinderRequiredDataProvider>
                    </LoungeFinderErrorBoundary>
                    <RenderModule moduleName="axp-footer" />
                  </ModalProvider>
                </CustomTrackProvider>
              </AnalyticsWrapper>
            </IntlProvider>
          </routeParamsContext.Provider>
        </queryParamsContext.Provider>
      </OneFetchyeProvider>
    </ErrorReporter>
  );
};

TravelLoungeFinderRoot.propTypes = {
  locale: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  params: PropTypes.shape({
    product: PropTypes.string,
    loungeId: PropTypes.string,
    locale: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
    hash: PropTypes.string,
  }).isRequired,
};

export const TestableTravelLoungeFinderRoot = TravelLoungeFinderRoot;

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
TravelLoungeFinderRoot.childRoutes = childRoutes;

// uncomment the following line to enable mock data
// global.mock = mockData;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line n/global-require, @typescript-eslint/no-require-imports -- require needs to be inside browser check
  TravelLoungeFinderRoot.appConfig = require('../appConfig').default;
}
// https://one-app.aexp.com/docs/documentation/how-tos/performance-optimization/server-tracing
export const loadModuleData = async ({
  store: { dispatch, getState },
  ownProps,
  fetchClient,
}) => {
  if (global.BROWSER) {
    // Expose state to window object for debugging
    global.getState = getState;
    // Global AmEx footer is missing required classes in non-US locales which results in a mostly unstyled footer.
    // Adding required classes for the locales where they're missing.
    const oneNavfooterElem = document.querySelector(
      '[data-module-name="axp-footer"]'
    );
    const oneNavFooterWrapper = oneNavfooterElem.parentElement;
    oneNavFooterWrapper.classList.add(
      'axp-footer__dls-module__module___1_EeR',
      'noindex'
    );
    oneNavFooterWrapper.classList.remove('pad-4');
    // `axp-footer__dls-module__module___1_EeR`
  }
  const product = ownProps?.params?.product;
  const locale = ownProps?.params?.locale;
  const airportCode = ownProps?.params?.airportCode;
  const isCulinaryCollectivePageEnabled = canViewTCL({
    state: getState(),
    feature: TclMigrationFeatures.CULINARY_COLLECTIVE_PAGE,
  });
  const isCenturionNetworkPageEnabled = canViewTCL({
    state: getState(),
    feature: TclMigrationFeatures.CENTURION_NETWORK_PAGE,
  });

  try {
    const resolution = await Promise.allSettled([
      dispatch(
        composeModules([{ name: 'axp-global-header' }, { name: 'axp-footer' }])
      ),
      dispatch(
        loadOneCmsLocaleAirports(
          locale || ownProps?.location?.query?.locale || 'en-US'
        )
      ),
      dispatch(loadOneCmsLocaleAirportLounges),
      dispatch(loadOneCmsLocaleCommonContent),
      dispatch(loadOneCmsLocaleProductContent(product)),
      dispatch(isAuthenticatedAndLoadWallet({ fetchClient })),
    ]);
    await Promise.all(
      resolution
        .filter((result) => result.status === 'rejected')
        .map(async (result) =>
          dispatch(
            addErrorToReport(result.reason, {
              collectionMethod: 'loadModuleData',
              moduleID: 'travel-lounge-finder-root',
              caught: true,
            })
          )
        )
    );
    await dispatch(loadBdaasAirports(product));
    if (airportCode) {
      await dispatch(loadBdaasAirportLounges(product, airportCode));
    }
    if (isCulinaryCollectivePageEnabled) {
      await dispatch(loadCdaasCulinaryCollectiveContent);
    }
    if (isCenturionNetworkPageEnabled) {
      await dispatch(loadCdaasCenturionHomepageContent);
      await dispatch(
        loadBdaasAirportLoungesByExperienceType('CENTURION,SIDECAR')
      );
    }
  } catch (e) {
    await dispatch(
      addErrorToReport(e, {
        collectionMethod: 'loadModuleData',
        moduleID: 'travel-lounge-finder-root',
        caught: false,
      })
    );
  }
};

TravelLoungeFinderRoot.holocron = {
  name: 'travel-lounge-finder-root',
  loadModuleData,
  reducer: combineReducers({
    auth: authReducer,
    lastViewedProduct: lastViewedProductReducer,
    lounge: loungeReducer,
    wallet: walletReducer,
    fetchye: OneCache().reducer,
  }),
};

export default oneAppModuleWrapper('travel-lounge-finder-root')(
  TravelLoungeFinderRoot
);
